import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import Button from '@mui/material/Button';
import type { GridColDef, GridFilterModel, GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import CustomDataGrid from 'components/common/CustomDataGrid';
import type React from 'react';
import { useMemo, useState } from 'react';
import type { Survey } from 'services/types/survey';
import { NAVY, RADIUS_ITEM, SURFACE_HOVER } from 'styles/tokens';

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pendiente',
  finished: 'Finalizada',
  aborted: 'Abortada',
};

const formatDate = (value: unknown): string => {
  if (!value) return '-';
  return new Date(value as string).toLocaleString('es-CL');
};

const answeredCount = (survey: Survey): number => survey.results?.filter((r) => r.response).length ?? 0;

// A question column is identified by (survey name, question position). Within a survey
// name, question N is always the same question, even if its text varies slightly between
// instances — so answers are matched by position, not by exact question text.
interface QuestionColumn {
  name: string;
  index: number;
  question: string;
}

const responseForColumn = (survey: Survey, column: QuestionColumn): string =>
  survey.name === column.name ? (survey.results?.[column.index]?.response ?? '') : '';

const QUESTION_FIELD_PREFIX = 'question__';

const baseColumns: GridColDef[] = [
  { field: 'id', headerName: 'ID', width: 70 },
  { field: 'name', headerName: 'Encuesta', width: 220 },
  {
    field: 'status',
    headerName: 'Estado',
    width: 120,
    valueFormatter: (value: string) => STATUS_LABELS[value] ?? value,
  },
  {
    field: 'rut',
    headerName: 'RUT',
    width: 130,
    valueGetter: (_value: unknown, row: Survey) => row.client?.rut ?? '-',
  },
  {
    field: 'phone',
    headerName: 'Teléfono',
    width: 140,
    valueGetter: (_value: unknown, row: Survey) => row.client?.phone ?? '-',
  },
  {
    field: 'branchOffice',
    headerName: 'Sucursal',
    width: 170,
    valueGetter: (_value: unknown, row: Survey) => row.lead?.branchOffice ?? '-',
  },
  {
    field: 'source',
    headerName: 'Origen',
    width: 150,
    valueGetter: (_value: unknown, row: Survey) => row.lead?.source ?? '-',
  },
  {
    field: 'answeredCount',
    headerName: 'Respuestas',
    width: 120,
    valueGetter: (_value: unknown, row: Survey) => answeredCount(row),
  },
  {
    field: 'createdAt',
    headerName: 'Creada',
    width: 170,
    valueFormatter: formatDate,
  },
  {
    field: 'updatedAt',
    headerName: 'Actualizada',
    width: 170,
    valueFormatter: formatDate,
  },
];

// CSV value escaping (semicolon separated, Excel-friendly for es-CL).
const escapeCsv = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  const str = String(value);
  if (str.includes(';') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

interface SurveysResultsTableProps {
  surveys: Survey[];
  onSurveyClick?: (survey: Survey) => void;
}

const SurveysResultsTable: React.FC<SurveysResultsTableProps> = ({ surveys, onSurveyClick }) => {
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 50 });
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });

  // Question/answer columns only make sense for a single survey — answers are matched by
  // question position, which is comparable only within one survey name. When the list mixes
  // several surveys (no specific survey filtered), there are no question columns.
  const questionColumns = useMemo<QuestionColumn[]>(() => {
    const names = new Set(surveys.map((s) => s.name));
    if (names.size !== 1) return [];

    const seen = new Set<number>();
    const ordered: QuestionColumn[] = [];
    for (const survey of surveys) {
      (survey.results ?? []).forEach((result, index) => {
        if (!result.question || seen.has(index)) return;
        seen.add(index);
        ordered.push({ name: survey.name, index, question: result.question });
      });
    }
    return ordered.sort((a, b) => a.index - b.index);
  }, [surveys]);

  const columns = useMemo<GridColDef[]>(() => {
    const dynamicColumns: GridColDef[] = questionColumns.map((column, index) => ({
      field: `${QUESTION_FIELD_PREFIX}${index}`,
      headerName: column.question,
      width: 260,
      sortable: false,
      valueGetter: (_value: unknown, row: Survey) => responseForColumn(row, column) || '-',
    }));
    return [...baseColumns, ...dynamicColumns];
  }, [questionColumns]);

  const pagedRows = useMemo(() => {
    const start = paginationModel.page * paginationModel.pageSize;
    return surveys.slice(start, start + paginationModel.pageSize);
  }, [surveys, paginationModel]);

  const handleDownload = () => {
    const headers = [
      'ID',
      'Encuesta',
      'Estado',
      'RUT',
      'Teléfono',
      'Sucursal',
      'Origen',
      'Respuestas',
      'Creada',
      'Actualizada',
      ...questionColumns.map((column) => column.question),
    ];

    const rows = surveys.map((survey) =>
      [
        survey.id,
        survey.name,
        STATUS_LABELS[survey.status] ?? survey.status,
        survey.client?.rut ?? '',
        survey.client?.phone ?? '',
        survey.lead?.branchOffice ?? '',
        survey.lead?.source ?? '',
        answeredCount(survey),
        survey.createdAt ? new Date(survey.createdAt).toLocaleString('es-CL') : '',
        survey.updatedAt ? new Date(survey.updatedAt).toLocaleString('es-CL') : '',
        ...questionColumns.map((column) => responseForColumn(survey, column)),
      ]
        .map(escapeCsv)
        .join(';')
    );

    const csvContent = [headers.map(escapeCsv).join(';'), ...rows].join('\n');
    const blob = new Blob([`﻿${csvContent}`], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', `encuestas_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };

  const downloadButton = (
    <Button
      onClick={handleDownload}
      disabled={surveys.length === 0}
      startIcon={<FileDownloadOutlinedIcon />}
      sx={{
        ml: 'auto',
        textTransform: 'none',
        fontFamily: 'Jost, sans-serif',
        fontWeight: 600,
        fontSize: '0.8rem',
        color: NAVY,
        borderRadius: RADIUS_ITEM,
        px: 1.5,
        '&:hover': { backgroundColor: SURFACE_HOVER },
      }}
    >
      Descargar CSV
    </Button>
  );

  return (
    <CustomDataGrid<Survey>
      rows={pagedRows}
      columns={columns}
      filterModel={filterModel}
      sortModel={sortModel}
      onFilterModelChange={setFilterModel}
      onSortModelChange={setSortModel}
      paginationModel={paginationModel}
      onPaginationModelChange={setPaginationModel}
      rowCount={surveys.length}
      onRowClick={onSurveyClick ? (row) => onSurveyClick(row) : undefined}
      getRowClassName={() => ''}
      pageId="surveys-results"
      topBar={downloadButton}
      disableFilters
    />
  );
};

export default SurveysResultsTable;
